import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import api from '../api';
import { FiArrowLeft, FiEye, FiEyeOff, FiShield, FiUsers, FiStar } from 'react-icons/fi';

const C = {
  rose: '#e3425c',
  roseLight: 'rgba(227,66,92,0.08)',
  gold: '#d4a853',
  goldLight: 'rgba(212,168,83,0.15)',
  goldDark: '#b8923e',
  dark: '#1c1c1e',
  cream: '#f7f7f8',
  creamDark: '#d4d4d8',
  white: '#ffffff',
  gray: '#8e8e93',
  grayLight: '#aeaeb2',
  green: '#10B981',
  red: '#ef4444',
};

const GOOGLE_CLIENT_ID = '1016709299418-2jef34saur52hhiuq5q3skg0fh48rpfu.apps.googleusercontent.com';
const FACEBOOK_APP_ID = '1645914549749760';

export default function VendorLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [method, setMethod] = useState('email');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const googleBtnRef = React.useRef(null);
  const [googleFallback, setGoogleFallback] = useState(false);

  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeUser, setWelcomeUser] = useState(null);

  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const demoEmail = searchParams.get('email');
    const demoPassword = searchParams.get('password');
    if (demoEmail) setEmail(demoEmail);
    if (demoPassword) setPassword(demoPassword);
  }, [searchParams]);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const showWelcomeAndRedirect = (user) => {
    setWelcomeUser(user);
    setShowWelcome(true);
    setTimeout(() => {
      window.location.href = (user.role === 'vendor' || user.role === 'both') ? '/vendor-dashboard' : '/dashboard';
    }, 10000);
  };

  // Google Sign-In
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 10;

    const tryRender = () => {
      if (cancelled) return;
      if (!window.google?.accounts?.id) {
        attempts++;
        if (attempts < MAX_ATTEMPTS) {
          setTimeout(tryRender, 300);
        } else {
          console.warn('Google GSI not available after max attempts');
        }
        return;
      }
      if (!googleBtnRef.current) {
        attempts++;
        if (attempts < MAX_ATTEMPTS) {
          setTimeout(tryRender, 200);
        }
        return;
      }
      try {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
        });
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          width: '100%',
          text: 'continue_with',
          shape: 'rectangular',
          logo_alignment: 'left',
        });
      } catch (e) {
        console.error('Google GSI render error:', e);
      }
    };

    if (window.google?.accounts?.id) {
      tryRender();
    } else {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (!cancelled) tryRender();
      };
      script.onerror = () => console.error('Failed to load Google GSI script');
      document.head.appendChild(script);
    }

    // Fallback: show custom button if GSI doesn't render within 4s
    const fallbackTimer = setTimeout(() => {
      if (!cancelled && googleBtnRef.current && googleBtnRef.current.children.length === 0) {
        setGoogleFallback(true);
      }
    }, 4000);

    return () => {
      cancelled = true;
      clearTimeout(fallbackTimer);
    };
  }, []);

  // Facebook SDK
  useEffect(() => {
    if (!FACEBOOK_APP_ID) return;
    window.fbAsyncInit = function () {
      window.FB.init({ appId: FACEBOOK_APP_ID, cookie: true, xfbml: false, version: 'v21.0' });
    };
    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }, []);

  const handleGoogleResponse = async (response) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/auth/google', { credential: response.credential });
      localStorage.setItem('ggr_token', res.data.token);
      localStorage.setItem('ggr_user', JSON.stringify(res.data.user));
      showWelcomeAndRedirect(res.data.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Google sign-in failed. Please try again.');
    }
    setLoading(false);
  };

  const handleFacebookClick = () => {
    if (!window.FB) {
      setError('Facebook SDK is loading. Please try again in a moment.');
      return;
    }
    window.FB.login(function (response) {
      if (response.authResponse) {
        const { accessToken, userID } = response.authResponse;
        window.FB.api('/me', { fields: 'name,email,picture.type(large)' }, async function (profile) {
          setLoading(true);
          setError('');
          try {
            const res = await api.post('/auth/facebook', {
              accessToken, userID, name: profile.name, email: profile.email, picture: profile.picture?.data?.url || '',
            });
            localStorage.setItem('ggr_token', res.data.token);
            localStorage.setItem('ggr_user', JSON.stringify(res.data.user));
            showWelcomeAndRedirect(res.data.user);
          } catch (err) {
            setError(err.response?.data?.error || 'Facebook sign-in failed. Please try again.');
          }
          setLoading(false);
        });
      }
    }, { scope: 'public_profile,email' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { email, password });
      localStorage.setItem('ggr_token', res.data.token);
      localStorage.setItem('ggr_user', JSON.stringify(res.data.user));
      showWelcomeAndRedirect(res.data.user);
    } catch (err) {
      let errorMessage = 'Login failed. Please check your credentials and try again.';
      if (err.response?.data?.error) errorMessage = err.response.data.error;
      else if (err.message) errorMessage = err.message;
      setError(errorMessage);
    }
    setLoading(false);
  };

  const sendOtp = async () => {
    if (!email) { setError('Please enter your email address'); return; }
    setError('');
    setOtpLoading(true);
    try {
      await api.post('/auth/phone/send-otp', { phone: email, email });
      setOtpSent(true);
      setCountdown(60);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send code. Please try again.');
    }
    setOtpLoading(false);
  };

  const verifyOtp = async () => {
    if (!otp || otp.length !== 6) { setError('Please enter the 6-digit code'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/auth/phone/verify', { phone: email, otp, email });
      localStorage.setItem('ggr_token', res.data.token);
      localStorage.setItem('ggr_user', JSON.stringify(res.data.user));
      showWelcomeAndRedirect(res.data.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: C.dark }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Inter:wght@400;500;600;700&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&display=swap');
        * { box-sizing: border-box; }
        .vendor-login-input { width: 100%; padding: 13px 16px; border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; background: rgba(255,255,255,0.04); color: white; font-size: 14px; font-family: 'Inter', sans-serif; outline: none; transition: border-color 0.2s, box-shadow 0.2s; }
        .vendor-login-input:focus { border-color: ${C.gold}; box-shadow: 0 0 0 3px ${C.goldLight}; }
        .vendor-login-input::placeholder { color: rgba(255,255,255,0.25); }
        .reg-social { display: flex; align-items: center; justify-content: center; gap: 10px; width: 100%; padding: 12px 16px; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; font-family: 'Inter', sans-serif; transition: all 0.2s; }
        .reg-divider { display: flex; align-items: center; gap: 14px; margin: 20px 0; color: rgba(255,255,255,0.3); font-size: 13px; }
        .reg-divider div { flex: 1; height: 1px; background: rgba(255,255,255,0.08); }
        @media (max-width: 768px) { .reg-left { display: none !important; } }
        @keyframes welcomeIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        @keyframes loadBar { 0% { width: 0%; } 100% { width: 100%; } }
        @keyframes gradientShift { 0%, 100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
        @keyframes giftBoxFloat { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.04); } }
        @keyframes textBounce { 0% { opacity: 0; transform: translateY(20px); } 60% { opacity: 1; transform: translateY(-6px); } 80% { transform: translateY(3px); } 100% { opacity: 1; transform: translateY(0); } }
        @keyframes textFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes shimmerMove { 0% { transform: translateX(-100%) skewX(-20deg); } 100% { transform: translateX(250%) skewX(-20deg); } }
      `}</style>

      {/* Welcome Loading Screen */}
      {showWelcome && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', background: '#ffffff', animation: 'welcomeIn 0.6s cubic-bezier(0.22, 1, 0.36, 1)' }}>
          <div style={{ animation: 'giftBoxFloat 2.5s ease-in-out infinite', marginBottom: '24px' }}>
            <img src="/GGR_GiftBox_Vector.png" alt="Grand Gather" style={{ width: '120px', height: '120px', display: 'block', objectFit: 'contain' }} />
          </div>
          <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 'clamp(24px, 5vw, 34px)', color: '#1C1C1E', marginBottom: '10px', textAlign: 'center', padding: '0 20px', animation: 'textBounce 0.6s ease-out' }}>
            Welcome, {welcomeUser?.name?.split(' ')[0] || 'there'}!
          </h2>
          <p style={{ fontSize: 'clamp(14px, 2.5vw, 16px)', color: 'rgba(0,0,0,0.55)', textAlign: 'center', maxWidth: '340px', lineHeight: 1.6, marginBottom: '32px', padding: '0 20px', animation: 'textFadeIn 0.8s ease-out 0.2s both' }}>
            Getting everything ready for you...
          </p>
          <div style={{ width: 'clamp(200px, 45vw, 300px)', height: '6px', background: 'rgba(0,0,0,0.06)', borderRadius: '6px', overflow: 'hidden', position: 'relative', boxShadow: '0 0 24px rgba(212,168,83,0.4)' }}>
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, #C9A84C, #D4A853, #E8D59A, #C9A84C, #D4A853, #E8D59A, #C9A84C)', backgroundSize: '300% 100%', borderRadius: '6px', animation: 'loadBar 10s ease-out forwards, gradientShift 3s ease-in-out infinite' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)', transform: 'translateX(-100%) skewX(-20deg)', animation: 'shimmerMove 2s ease-in-out infinite' }} />
          </div>
        </div>
      )}

      {/* Left: Branding Panel */}
      <div className="reg-left" style={{ flex: '0 0 42%', background: `linear-gradient(145deg, ${C.goldDark} 0%, ${C.gold} 100%)`, padding: '48px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-120px', right: '-80px', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.06)' }} />
        <div style={{ position: 'absolute', bottom: '-80px', left: '-60px', width: '220px', height: '220px', borderRadius: '50%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }} />

        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', marginBottom: '48px', position: 'relative' }}>
          <FiArrowLeft size={16} color="rgba(255,255,255,0.7)" />
          <img src="/ggr-logo-deck.png" alt="GGR" style={{ height: '44px', width: 'auto', filter: 'brightness(10)' }} />
        </Link>

        <div style={{ position: 'relative' }}>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(28px, 3vw, 40px)', color: C.white, lineHeight: 1.15, marginBottom: '16px', fontWeight: 700 }}>
            Vendor Portal
          </h1>
          <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.7, maxWidth: '360px', marginBottom: '40px' }}>
            Sign in to manage your store, services, bookings, and connect with event hosts across South Africa.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {[
              { icon: <FiUsers size={18} />, text: 'Manage bookings & customer chats' },
              { icon: <FiStar size={18} />, text: '4.9/5 rating from 500+ happy users' },
              { icon: <FiShield size={18} />, text: 'Secure, verified & trusted platform' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.white, flexShrink: 0 }}>
                  {item.icon}
                </div>
                <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ position: 'relative', marginTop: '48px', paddingTop: '24px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)', lineHeight: 1.6 }}>
            Don't have a vendor account?{' '}
            <Link to="/register/vendor" style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontWeight: 600 }}>Register here</Link>
            .
          </p>
        </div>
      </div>

      {/* Right: Form Panel */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ maxWidth: '440px', width: '100%' }}>
          {/* Mobile logo */}
          <div style={{ display: 'none', marginBottom: '32px' }} className="reg-mobile-logo">
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', justifyContent: 'center' }}>
              <FiArrowLeft size={16} color="rgba(255,255,255,0.7)" />
              <img src="/ggr-logo-deck.png" alt="Grand Gather Registry" style={{ height: '44px', width: 'auto', filter: 'brightness(10)' }} />
            </Link>
          </div>

          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '28px', color: C.white, marginBottom: '6px', fontWeight: 700 }}>
            Vendor Sign In
          </h2>
          <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.4)', marginBottom: '12px' }}>
            Sign in to manage your store and services
          </p>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '5px 14px', borderRadius: '20px', background: `${C.gold}15`, color: C.goldDark, fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '28px' }}>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke={C.goldDark} strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
              <rect x='2' y='7' width='20' height='14' rx='2' ry='2'/><path d='M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16'/>
            </svg>
            Vendor Login
          </div>

          {error && (
            <div style={{ padding: '12px', background: `${C.rose}15`, border: `1px solid ${C.rose}30`, borderRadius: '10px', color: C.rose, marginBottom: '16px', fontSize: '13px' }}>
              {error}
            </div>
          )}

          {/* Social Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '4px' }}>
            <div ref={googleBtnRef} style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              {/* Fallback button if GSI fails to render */}
              {googleFallback && (
                <button onClick={() => window.google?.accounts?.id?.prompt?.()} className="reg-social"
                  style={{ background: C.white, color: 'rgba(0,0,0,0.7)', border: '1px solid rgba(0,0,0,0.15)' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Sign in with Google
                </button>
              )}
            </div>

            <button onClick={handleFacebookClick} disabled={loading} className="reg-social"
              style={{ background: '#1877F2', color: C.white, border: 'none', opacity: loading ? 0.5 : 1 }}
              onMouseOver={e => e.currentTarget.style.background = '#166FE5'}
              onMouseOut={e => e.currentTarget.style.background = '#1877F2'}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Continue with Facebook
            </button>

            <button onClick={() => { setMethod(method === 'phone' ? 'email' : 'phone'); setError(''); }} className="reg-social"
              style={{ background: method === 'phone' ? '#10B981' : 'rgba(255,255,255,0.04)', color: method === 'phone' ? C.white : 'rgba(255,255,255,0.7)', border: method === 'phone' ? 'none' : '1px solid rgba(255,255,255,0.1)' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 4l-10 8L2 4"/>
              </svg>
              {method === 'phone' ? 'Switch to Email' : 'Sign in with Code'}
            </button>
          </div>

          {/* Divider */}
          <div className="reg-divider">
            <div />
            <span>or continue with email</span>
            <div />
          </div>

          {/* Email/Password Form */}
          {method === 'email' ? (
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.5)', marginBottom: '6px' }}>Email or Phone Number</label>
                  <input className="vendor-login-input" type="text" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="your@email.com or 0812345678" required />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.5)', marginBottom: '6px' }}>Password</label>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input className="vendor-login-input" type={showPassword ? 'text' : 'password'} value={password}
                      onChange={e => setPassword(e.target.value)} placeholder="Enter your password" required
                      style={{ flex: 1 }} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} style={{
                      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0 12px 12px 0',
                      cursor: 'pointer', color: 'rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: '0 14px', height: '48px', marginLeft: '-1px',
                    }}>
                      {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                    </button>
                  </div>
                </div>
              </div>

              <button type="submit" disabled={loading} style={{
                width: '100%', padding: '14px', marginTop: '20px',
                background: `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`,
                color: C.white, border: 'none', borderRadius: '12px',
                fontSize: '15px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1, transition: 'all 0.2s',
                fontFamily: 'Inter, sans-serif',
                boxShadow: `0 4px 16px ${C.goldLight}`,
              }}>
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
              <div style={{ textAlign: 'right', marginTop: '12px' }}>
                <Link to="/forgot-password" style={{ fontSize: '13px', color: C.gold, fontWeight: 500, textDecoration: 'none' }}>
                  Forgot password?
                </Link>
              </div>
            </form>
          ) : (
            <div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.5)', marginBottom: '6px' }}>Email Address</label>
                  <input className="vendor-login-input" type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="your@email.com" disabled={otpSent} />
                </div>
              </div>

              <button type="button" onClick={sendOtp} disabled={otpLoading || countdown > 0 || !email}
                style={{
                  width: '100%', padding: '13px', marginTop: '16px', marginBottom: '16px',
                  background: `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`,
                  color: C.white, border: 'none', borderRadius: '10px',
                  fontSize: '14px', fontWeight: 600, cursor: (otpLoading || countdown > 0 || !email) ? 'not-allowed' : 'pointer',
                  opacity: (otpLoading || countdown > 0 || !email) ? 0.5 : 1,
                  transition: 'all 0.2s', fontFamily: 'Inter, sans-serif',
                }}>
                {otpLoading ? 'Sending...' : countdown > 0 ? `Resend Code (${countdown}s)` : otpSent ? 'Resend Code' : 'Send Verification Code'}
              </button>

              {otpSent && (
                <>
                  <div style={{ padding: '12px', background: 'rgba(16,185,129,0.1)', borderRadius: '10px', marginBottom: '14px', fontSize: '13px', color: '#10B981', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    Code sent to {email}
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.5)', marginBottom: '6px' }}>6-Digit Code</label>
                    <input className="vendor-login-input" type="text" value={otp}
                      onChange={e => setOtp(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                      placeholder="000000" maxLength={6} autoFocus
                      style={{ textAlign: 'center', fontSize: '24px', letterSpacing: '8px', fontWeight: 700, fontFamily: 'monospace' }} />
                  </div>
                  <button type="button" onClick={verifyOtp} disabled={loading || otp.length !== 6} style={{
                    width: '100%', padding: '14px', marginTop: '4px',
                    background: `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`,
                    color: C.white, border: 'none', borderRadius: '12px',
                    fontSize: '15px', fontWeight: 600, cursor: (loading || otp.length !== 6) ? 'not-allowed' : 'pointer',
                    opacity: (loading || otp.length !== 6) ? 0.6 : 1,
                    transition: 'all 0.2s', fontFamily: 'Inter, sans-serif',
                  }}>
                    {loading ? 'Verifying...' : 'Verify & Sign In'}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Sign up link */}
          <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '14px', color: 'rgba(255,255,255,0.4)' }}>
            Don't have a vendor account?{' '}
            <Link to="/register/vendor" style={{ color: C.gold, fontWeight: 600, textDecoration: 'none' }}>Sign Up</Link>
          </p>

          {/* Switch to host login */}
          <p style={{ textAlign: 'center', marginTop: '8px', fontSize: '13px', color: 'rgba(255,255,255,0.3)' }}>
            Are you a host?{' '}
            <Link to="/login" style={{ color: C.rose, fontWeight: 500, textDecoration: 'none' }}>Sign in here</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
